import { useEffect, useRef, useState } from "react";
import { Download, FileUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/http";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { dateStyle: "medium" }); } catch { return iso; }
};

const Slot = ({ lang, title, hint, entry, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Choisissez un fichier PDF"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await http.post("/admin/cv", form, { params: { lang }, timeout: 120000 });
      onChange(data);
      toast.success("CV mis en ligne");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Retirer ce CV du site ?")) return;
    const { data } = await http.delete("/admin/cv", { params: { lang } });
    onChange(data);
    toast.success("CV retiré");
  };

  return (
    <div className="tile space-y-4 p-6" data-testid={`cv-slot-${lang}`}>
      <div>
        <h3 className="font-display text-lg text-[#F7F4EE]">{title}</h3>
        <p className="mt-1 text-xs text-[#C5BDB3]">{hint}</p>
      </div>
      {entry ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#D9CFC2]" data-testid={`cv-current-${lang}`}>
          <span className="truncate font-medium text-[#F7F4EE]">{entry.filename}</span>
          <span className="text-xs text-[#C5BDB3]">· {(entry.size / 1024 / 1024).toFixed(1)} Mo · {fmtDate(entry.uploaded_at)}</span>
        </div>
      ) : (
        <p className="text-sm text-[#C5BDB3]" data-testid={`cv-empty-${lang}`}>Aucun fichier pour le moment.</p>
      )}
      <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`cv-input-${lang}`} />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => input.current?.click()} disabled={busy} className="btn-gold" data-testid={`cv-upload-${lang}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}{entry ? "Remplacer le PDF" : "Envoyer mon PDF"}
        </button>
        {entry && (
          <>
            <a href={`${BACKEND}/api/cv.pdf?lang=${lang}`} className="chip" data-testid={`cv-preview-${lang}`}><Download className="h-4 w-4" />Télécharger</a>
            <button onClick={remove} className="chip" data-testid={`cv-delete-${lang}`}><Trash2 className="h-4 w-4" />Retirer</button>
          </>
        )}
      </div>
    </div>
  );
};

export const CvManager = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    http.get("/cv/info").then((r) => setInfo(r.data)).catch(() => setInfo({ fr: null, en: null }));
  }, []);
  if (!info) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[#E69C24]" />;
  return (
    <div className="max-w-3xl space-y-5" data-testid="cv-manager">
      <p className="text-sm leading-relaxed text-[#C5BDB3]">
        Déposez votre propre CV au format PDF (15 Mo max). Le bouton « CV en PDF » apparaît dans la chambre et la version classique dès qu'un fichier est en ligne.
        Les visiteurs en anglais reçoivent la version anglaise si vous l'avez déposée, sinon la version française.
      </p>
      <div className="grid gap-5 md:grid-cols-2">
        <Slot lang="fr" title="CV en français" hint="Version principale, proposée par défaut." entry={info.fr} onChange={setInfo} />
        <Slot lang="en" title="CV en anglais" hint="Facultatif, pour les productions étrangères." entry={info.en} onChange={setInfo} />
      </div>
    </div>
  );
};
